import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { EVENT_TYPES } from "../lib/calendar";
import { AgendaList } from "../components/calendar/Views";
import EventDialog from "../components/EventDialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { usePageMeta } from "../hooks/usePageMeta";
import { useEventStructuredData } from "../hooks/useEventStructuredData";

export default function AllEventsPage() {
  usePageMeta(
    "All Upcoming Events",
    "The full line-up of upcoming TCG tournaments and events at Hex Labs Games in Maldon, Essex."
  );
  const [events, setEvents] = useState([]);
  const [scope, setScope] = useState("upcoming");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  useEventStructuredData(events);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  const list = useMemo(() => {
    const now = new Date();
    let rows = events.filter((e) => (scope === "upcoming" ? new Date(e.start) >= now : new Date(e.start) < now));
    if (filter !== "all") rows = rows.filter((e) => e.event_type === filter);
    rows.sort((a, b) =>
      scope === "upcoming" ? new Date(a.start) - new Date(b.start) : new Date(b.start) - new Date(a.start)
    );
    return rows;
  }, [events, scope, filter]);

  const grouped = useMemo(() => {
    const map = new Map();
    list.forEach((e) => {
      const key = format(new Date(e.start), "MMMM yyyy");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return [...map.entries()];
  }, [list]);

  return (
    <div className="mx-auto max-w-4xl px-5 lg:px-10 py-10 lg:py-16" data-testid="all-events-page">
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 hover:text-primary transition-colors duration-200" data-testid="back-to-calendar">
        <ArrowLeft className="h-4 w-4" /> Calendar
      </Link>

      <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tighter">
        FULL <span className="text-primary">LINE-UP</span>
      </h1>
      <p className="mt-4 text-base md:text-lg text-neutral-400 max-w-xl">
        Every event in date order. Tap any one for tickets and to add it to your calendar.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="bg-white/5 border border-white/10 rounded-full">
            <TabsTrigger value="upcoming" data-testid="scope-upcoming" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4">
              Upcoming
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="scope-past" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4">
              Past
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap gap-1">
          <button
            data-testid="all-events-filter-all"
            onClick={() => setFilter("all")}
            className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors duration-200 ${
              filter === "all" ? "border-primary text-primary" : "border-white/10 text-neutral-500 hover:text-white"
            }`}
          >
            All
          </button>
          {EVENT_TYPES.map((t) => (
            <button
              key={t.value}
              data-testid={`all-events-filter-${t.value}`}
              onClick={() => setFilter(t.value)}
              className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors duration-200"
              style={{
                borderColor: filter === t.value ? t.color : "rgba(255,255,255,0.1)",
                color: filter === t.value ? t.color : "#737373",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-6 font-mono text-xs text-neutral-500" data-testid="all-events-count">
        {list.length} event{list.length === 1 ? "" : "s"}
      </p>

      <div className="mt-4 space-y-8">
        {grouped.map(([month, rows]) => (
          <section key={month}>
            <h2 className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">{month}</h2>
            <AgendaList events={rows} onSelect={setSelected} />
          </section>
        ))}
        {list.length === 0 && (
          <p className="text-sm text-neutral-500" data-testid="all-events-empty">
            No {scope} events to show.
          </p>
        )}
      </div>

      <EventDialog event={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
